import { useEffect, useState } from "react";
import {
  Bluetooth,
  BluetoothConnected,
  BluetoothOff,
  Eye,
  Loader2,
  Printer,
} from "lucide-react";
import { usePrinter, LABEL_SIZE_PRESETS } from "@/context/PrinterContext";
import LabelPreviewDialog from "@/components/LabelPreviewDialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/**
 * Match the persisted label size against the preset list. A "custom" id
 * is returned when no preset shares the same width/height — that lets the
 * popover swap to numeric inputs without us tracking a separate flag.
 */
function presetIdFor(size) {
  const hit = LABEL_SIZE_PRESETS.find(
    (p) => !p.custom && p.width === size.width && p.height === size.height,
  );
  return hit?.id || "custom";
}

/**
 * Three-input row for custom label dimensions. Uses local draft string
 * state so the operator can clear a field and retype a fresh value
 * without the input snapping back to the persisted size mid-keystroke.
 * Commits to the store on **blur** (or Enter) — until then the draft
 * lives only in this component.
 */
function CustomSizeInputs({ labelSize, setLabelSize }) {
  const [draft, setDraft] = useState({
    width: String(Math.round(labelSize.width)),
    height: String(Math.round(labelSize.height)),
    gap: String(labelSize.gap),
  });

  // Re-sync draft whenever an external source (preset click, storage
  // hydration) changes the stored size — otherwise the displayed
  // numbers would lag behind the actual configuration.
  useEffect(() => {
    setDraft({
      width: String(Math.round(labelSize.width)),
      height: String(Math.round(labelSize.height)),
      gap: String(labelSize.gap),
    });
  }, [labelSize.width, labelSize.height, labelSize.gap]);

  const commit = () => setLabelSize({ ...labelSize, ...draft });

  return (
    <div className="grid grid-cols-3 gap-2 mt-2" data-testid="label-size-custom">
      <div>
        <Label className="text-[10px] text-stone-500">Width mm</Label>
        <Input
          type="number"
          inputMode="numeric"
          min={10}
          max={200}
          className="h-9"
          value={draft.width}
          onChange={(e) => setDraft((d) => ({ ...d, width: e.target.value }))}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
          data-testid="label-size-width"
        />
      </div>
      <div>
        <Label className="text-[10px] text-stone-500">Height mm</Label>
        <Input
          type="number"
          inputMode="numeric"
          min={10}
          max={300}
          className="h-9"
          value={draft.height}
          onChange={(e) => setDraft((d) => ({ ...d, height: e.target.value }))}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
          data-testid="label-size-height"
        />
      </div>
      <div>
        <Label className="text-[10px] text-stone-500">Gap mm</Label>
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          max={10}
          step="0.5"
          className="h-9"
          value={draft.gap}
          onChange={(e) => setDraft((d) => ({ ...d, gap: e.target.value }))}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
          data-testid="label-size-gap"
        />
      </div>
    </div>
  );
}

/**
 * Compact printer status chip rendered in the global header next to the
 * location filter. Click it to open a popover with full connect/disconnect
 * /test-print/label-size controls. The chip itself only shows status.
 */
export default function PrinterStatusChip() {
  const printer = usePrinter();
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  if (!printer) return null;

  const {
    status,
    deviceName,
    connect,
    disconnect,
    testPrint,
    lastError,
    labelSize,
    setLabelSize,
  } = printer;
  const matchedPresetId = presetIdFor(labelSize);
  // "Custom" mode is sticky once the operator picks it OR when the stored
  // size doesn't match any preset (e.g. after a custom save + reload).
  const isCustom = customMode || matchedPresetId === "custom";
  const selectValue = isCustom ? "custom" : matchedPresetId;

  // Chip styling/icon per state — every state keys off the same `status`
  // string so adding a new state means touching one switch.
  let label = "Connect printer";
  let chipClass = "border-stone-300 text-stone-600 bg-white hover:bg-stone-50";
  let Icon = Bluetooth;
  let testId = "printer-chip-disconnected";

  if (status === "unsupported") {
    label = "Printer N/A";
    chipClass = "border-stone-200 text-stone-400 bg-stone-50 cursor-not-allowed";
    Icon = BluetoothOff;
    testId = "printer-chip-unsupported";
  } else if (status === "connecting") {
    label = "Connecting…";
    chipClass = "border-amber-300 text-amber-700 bg-amber-50";
    Icon = Loader2;
    testId = "printer-chip-connecting";
  } else if (status === "connected") {
    label = deviceName || "Printer ready";
    chipClass = "border-emerald-300 text-emerald-700 bg-emerald-50";
    Icon = BluetoothConnected;
    testId = "printer-chip-connected";
  }

  const chip = (
    <button
      type="button"
      data-testid={testId}
      disabled={status === "unsupported"}
      className={`h-11 px-3 rounded-lg border flex items-center gap-2 text-sm font-semibold transition-colors ${chipClass}`}
      title={lastError ? `Last error: ${lastError}` : label}
    >
      <Icon
        className={`h-4 w-4 ${status === "connecting" ? "animate-spin" : ""}`}
      />
      <span className="hidden sm:inline max-w-[140px] truncate">{label}</span>
    </button>
  );

  if (status === "unsupported") return chip;

  const onPresetChange = (id) => {
    const preset = LABEL_SIZE_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    if (preset.custom) {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    setLabelSize({ width: preset.width, height: preset.height, gap: preset.gap });
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{chip}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80"
        data-testid="printer-popover"
      >
        <div className="flex items-start gap-2 mb-3">
          <Printer className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <div className="font-display text-base font-bold">Bluetooth printer</div>
            <div className="text-xs text-stone-500">
              {status === "connected"
                ? `Connected to ${deviceName}`
                : "No printer paired"}
            </div>
          </div>
        </div>

        {/* Label size — applies to every TSPL print regardless of pairing. */}
        <div className="mb-3" data-testid="label-size-section">
          <Label className="text-[11px] uppercase tracking-wider text-stone-500 font-bold">
            Label size
          </Label>
          <Select value={selectValue} onValueChange={onPresetChange}>
            <SelectTrigger className="h-10 mt-1" data-testid="label-size-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LABEL_SIZE_PRESETS.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`label-size-${p.id}`}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isCustom && (
            <CustomSizeInputs
              labelSize={labelSize}
              setLabelSize={setLabelSize}
            />
          )}
          <div className="text-[11px] text-stone-500 mt-1">
            Sent to printer as <code>SIZE {Math.round(labelSize.width)} mm,
            {Math.round(labelSize.height)} mm</code>.
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full mt-2"
            data-testid="printer-preview-button"
            onClick={() => {
              setPreviewOpen(true);
              setOpen(false);
            }}
          >
            <Eye className="h-4 w-4 mr-2" /> Preview label layout
          </Button>
        </div>

        {lastError && (
          <div className="mb-3 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">
            {lastError}
          </div>
        )}
        {status === "connected" ? (
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="printer-test-print"
              onClick={async () => {
                await testPrint();
              }}
            >
              Test print
            </Button>
            <Button
              type="button"
              variant="destructive"
              data-testid="printer-disconnect"
              onClick={async () => {
                await disconnect();
                setOpen(false);
              }}
            >
              Disconnect
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            className="w-full"
            data-testid="printer-connect"
            disabled={status === "connecting"}
            onClick={async () => {
              const ok = await connect();
              if (ok) setOpen(false);
            }}
          >
            {status === "connecting" ? "Connecting…" : "Pair Bluetooth printer"}
          </Button>
        )}
        <div className="mt-3 text-[11px] text-stone-500 leading-relaxed">
          Compatible with Beeprt BY-243BT and similar TSPL/TSC label
          printers. Use Chrome on Android — iPad Safari can&apos;t pair.
        </div>
      </PopoverContent>
    </Popover>
    <LabelPreviewDialog
      open={previewOpen}
      onOpenChange={setPreviewOpen}
      size={labelSize}
    />
    </>
  );
}
